// src/hooks/useInactivityLogout.js
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContagemTempo } from './useContagemTempo';

export const useInactivityLogout = (timeoutMinutes = 10) => {
  const navigate = useNavigate();
  const { pararTodasContagens } = useContagemTempo();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Função para registrar logout por inatividade
  const registrarLogoutInatividade = async () => {
    try {
      const accessToken = localStorage.getItem('access');
      if (accessToken) {
        await fetch(`${process.env.REACT_APP_API_BASE}/api/register-logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            motivo: 'inatividade',
            tempo_inativo: timeoutMinutes,
            refresh: localStorage.getItem('refresh')  // D-03: backend invalida o refresh
          })
        });
      }
    } catch (error) {
      console.error('Erro ao registrar logout por inatividade:', error);
    }
  };

  // Função para fazer logout
  const handleInactivityLogout = useCallback(async () => {
    console.log('Logout por inatividade - 10 minutos sem atividade');
    
    try {
      // Parar todas as contagens ativas
      await pararTodasContagens();
      
      // Registrar o logout por inatividade
      await registrarLogoutInatividade();
      
      // Limpar localStorage
      localStorage.removeItem('access');
      localStorage.removeItem('refresh');
      localStorage.removeItem('firstName');
      localStorage.removeItem('animacaoAtual');
      
      // Mostrar alerta para o usuário
      alert('Sessão expirada por inatividade. Você será redirecionado para a página de login.');
      
      // Redirecionar para login
      navigate('/login');
    } catch (error) {
      console.error('Erro durante logout por inatividade:', error);
      navigate('/login');
    }
  }, [navigate, pararTodasContagens, timeoutMinutes]);

  // Função para mostrar aviso antes do logout
  const showWarning = useCallback(() => {
    const remainingTime = 2; // 2 minutos de aviso
    
    const userResponse = window.confirm(
      `ATENÇÃO: Sua sessão expirará em ${remainingTime} minutos por inatividade.\n\n` +
      'Clique em "OK" para continuar usando o sistema ou "Cancelar" para fazer logout agora.'
    );
    
    if (userResponse) {
      // Usuário quer continuar - resetar timer
      resetTimer();
    } else {
      // Usuário quer fazer logout agora
      handleInactivityLogout();
    }
  }, [handleInactivityLogout]);

  // Função para resetar o timer
  const resetTimer = useCallback(() => {
    // Limpar timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // Atualizar última atividade
    lastActivityRef.current = Date.now();
    
    // Timer para aviso (8 minutos)
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, (timeoutMinutes - 2) * 60 * 1000);
    
    // Timer para logout automático (10 minutos)
    timeoutRef.current = setTimeout(() => {
      handleInactivityLogout();
    }, timeoutMinutes * 60 * 1000);
    
  }, [timeoutMinutes, showWarning, handleInactivityLogout]);

  // Função para detectar atividade do usuário
  const detectActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Só resetar se passou pelo menos 1 segundo desde a última atividade
    // (evita múltiplos resets desnecessários)
    if (timeSinceLastActivity > 1000) {
      resetTimer();
    }
  }, [resetTimer]);

  // Eventos que consideramos como "atividade"
  const activityEvents = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'focus'
  ];

  useEffect(() => {
    // Verificar se o usuário está logado
    const token = localStorage.getItem('access');
    if (!token) {
      return;
    }

    // Iniciar o timer
    resetTimer();

    // Adicionar listeners para detectar atividade
    activityEvents.forEach(event => {
      document.addEventListener(event, detectActivity, true);
    });

    // Interceptar requisições AJAX para considerar como atividade
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      detectActivity();
      return originalFetch.apply(this, args);
    };

    // Interceptar XMLHttpRequest para considerar como atividade
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(...args) {
      detectActivity();
      return originalOpen.apply(this, args);
    };

    // Cleanup
    return () => {
      // Limpar timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      
      // Remover listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, detectActivity, true);
      });
      
      // Restaurar fetch original
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalOpen;
    };
  }, [detectActivity, resetTimer]);

  // Retornar função para resetar manualmente (útil para após login)
  return { resetTimer };
};